import * as React from 'react';
import styled from 'styled-components';
import {getTags as getCoursesTags} from 'api/course';
import {getTags as getLessonsTags} from 'api/lesson/requests';
import {getTags as getProgramsTags} from 'api/program/requests';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/isError';
import {Control, ControlVariant} from './components/Control';
import Input, {InputVariant} from 'components/Input';
import Dropdown, {DropdownAlign, DropdownSide} from 'components/Dropdown';
import {ErrorObject, Tag} from 'types';
import {TagBuilderProps} from './types';

const Selected = styled.div`
  flex-wrap: wrap;
  display: flex;
`;

/**
 * The tag builder component.
 * @param {TagBuilderProps} props
 * @return {React.ReactElement<TagBuilderProps>}
 * @constructor
 */
export default function TagBuilder(
    props: TagBuilderProps,
): React.ReactElement<TagBuilderProps> {
  const [
    tags, setTags,
  ] = React.useState<Tag[]>([]);

  const [
    selected, setSelected,
  ] = React.useState<Tag[]>(props.tags || []);

  const [
    inputValue, setInputValue,
  ] = React.useState<string>('');

  /**
   * The hook of set selected action.
   * @param {Tag[]} tags
   */
  function _setSelected(tags: Tag[]): void {
    setSelected(tags);
    props.onChange && props.onChange(tags);
  }

  /**
   * Fetch the tags.
   * @return {Promise<void>}
   */
  function fetch(): void {
    /**
     * Update the tags store.
     * @param {Tag[] | ErrorObject | undefined} response
     */
    function updateTags(response: Tag[] | undefined | ErrorObject): void {
      validateResponse(response);

      if (response && !isError(response)) {
        setTags(response);
      }
    }

    switch (props.type) {
      case 'courses': {
        getCoursesTags().then(updateTags)
        break;
      }
      case 'lessons': {
        getLessonsTags().then(updateTags)
        break;
      }
      case 'programs': {
        getProgramsTags().then(updateTags)
        break;
      }
    }
  }

  React.useEffect(() => {
    fetch();
  }, []);

  /**
   * Render input tag.
   * @param {callback} open
   * @param {callback} close
   * @return {JSX.Element}
   */
  function renderInput(
    open: () => void,
    close: () => void,
  ): JSX.Element {
    /**
     * This form submit event handler.
     * @param {React.FormEvent} event
     */
    function handleKeyPress(event: React.KeyboardEvent): void {
      if (event.key === 'Enter') {
        _setSelected([
          ...selected,
          inputValue,
        ]);
        setInputValue('');
      }

      close();
    }

    /**
     * This tag change event handler.
     * @param {React.ChangeEvent<HTMLInputElement>} event
     */
    function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
      setInputValue(event.target.value);
    }

    /**
     * This tag focus event handler.
     */
    function handleFocus(): void {
      open();
    }

    return (
      <Input
        value={inputValue}
        variant={InputVariant.Accent}
        placeholder="Введите тег и нажмите Enter"
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        onFocus={handleFocus}
        {...props.inputProps}
      />
    );
  }

  /**
   * Render dropdown.
   * @return {JSX.Element}
   */
  function renderDropdown(): JSX.Element {
    /**
     * Render tag.
     * @param {Tag} tag
     * @param {number} index
     * @return {JSX.Element}
     */
    function renderTag(tag: Tag, index: number): JSX.Element {
      /**
       * This select tag event handler.
       */
      function handleSelect(): void {
        _setSelected([
          ...selected,
          tag,
        ]);
      }

      return (
        <Dropdown.Item
          key={index}
          as="button"
          onClick={handleSelect}>
          {tag}
        </Dropdown.Item>
      );
    }

    return (
      <Dropdown
        trigger={renderInput}
        side={DropdownSide.Bottom}
        align={DropdownAlign.Center}>
        {tags ? tags.map((tag, index) => (
          renderTag(tag, index)
        )) : []}
      </Dropdown>
    );
  }

  /**
   * Render selected tag's.
   * @return {JSX.Element | undefined}
   */
  function renderSelected(): JSX.Element | undefined {
    /**
     * Render selected tag.
     * @param {Tag} tag
     * @param {number} index
     * @return {JSX.Element}
     */
    function renderSelectedTag(tag: Tag, index: number): JSX.Element {
      /**
       * This remove event handler.
       */
      function handleRemove(): void {
        _setSelected(selected.filter((selectedTag) => (
          selectedTag !== tag
        )));
      }

      return (
        <Control
          key={index}
          variant={ControlVariant.Accent}
          onClick={handleRemove}>
          {tag}
        </Control>
      );
    }

    /**
     * Render remove control.
     * @return {JSX.Element}
     */
    function renderRemove(): JSX.Element {
      /**
       * This remove event handler.
       */
      function handleRemove(): void {
        _setSelected([]);
      }

      return (
        <Control onClick={handleRemove}>
          Очистить все
        </Control>
      );
    }

    return selected.length > 0 ? (
      <Selected>
        {selected.map((tag, index) => (
          renderSelectedTag(tag, index)
        ))}
        {renderRemove()}
      </Selected>
    ) : undefined;
  }

  return (
    <React.Fragment>
      {renderDropdown()}
      {renderSelected()}
    </React.Fragment>
  );
}
